import terminology, { modelTerminology } from 'javascripts/terminology';
import RecordAction, { type RemoteAttributes } from '../RecordAction';
import { isPresent } from 'javascripts/util/isPresent';
import {
  Page,
  Feature,
  Epic,
  Requirement,
  Idea,
} from 'components/framework/models';
import { showNoticeMessage } from 'javascripts/messages';
import { jsonFetch } from 'javascripts/fetch';
import type ApplicationModel from 'components/framework/ApplicationModel';
import { type MenuContent } from 'components/SelectionController/ActionMenuItem';

type CopyRecordType = Epic | Feature | Requirement | Page;

export class CopyRecordAction extends RecordAction<CopyRecordType> {
  recordTypes = ['Epic', 'Feature', 'Requirement', 'Page', 'Idea'];
  isRemoteLink = !this.multipleSelections();

  get title() {
    if (this.singleRecordType) {
      const recordType = modelTerminology(this.firstRecord!, {
        count: this.selectedRecords.length,
        lowercase: true,
      });
      return `Copy ${recordType}`;
    } else {
      return `Copy ${this.selectedRecords.length} records`;
    }
  }

  get icon() {
    return 'fa-regular fa-clone';
  }

  get hasSubmenu() {
    const { firstRecord } = this;
    if (!firstRecord) return false;

    // Show submenu for epics
    if (firstRecord.typename === 'Epic') return true;

    // If the record is a page, we only want to show the submenu if it has a document date, or if it's a single document we allow copying w/ children
    if (firstRecord.typename === 'Page') {
      return (
        (this.selectedRecords.length === 1 && firstRecord.childrenCount > 0) ||
        isPresent(firstRecord.documentDate)
      );
    }

    return false;
  }

  prepareSubmenuItems(): MenuContent {
    if (this.firstRecord?.typename === 'Epic') {
      const epic = this.firstRecord;
      const basePath = `/epics/${epic.referenceNum}/duplicate.drawer`;
      const epicTerm = modelTerminology(epic, { lowercase: true });
      const featuresTerm = terminology('features', {
        isPlural: true,
        lowercase: true,
      });

      return [
        {
          label: `Copy ${epicTerm} with ${featuresTerm}`,
          data: {
            href: `${basePath}?copy_features=true`,
            urlParams: { copy_features: 'true' },
          },
        },
        {
          label: `Copy ${epicTerm} without ${featuresTerm}`,
          data: {
            href: `${basePath}?copy_features=false`,
            urlParams: { copy_features: 'false' },
          },
        },
      ];
    }

    if (this.firstRecord?.typename === 'Page') {
      const page = this.firstRecord;
      const prefix = page.isPersonal ? '/user/pages' : '/pages';

      const menuGroups: MenuContent = {};

      if (this.firstRecord.documentDate) {
        menuGroups['Document date'] = [
          {
            label: 'Same date',
            data: {},
          },
          {
            label: 'Following week',
            data: {
              href: `${prefix}/${page.referenceNum}/duplicate?next_week=true`,
            },
          },
        ];
      }

      if (this.selectedRecords.length === 1) {
        menuGroups['Child documents'] = [
          {
            label: 'Copy with child documents',
            data: {
              href: `${prefix}/${page.referenceNum}/duplicate?descendants=true`,
            },
          },
          {
            label: 'Copy without child documents',
            data: {
              href: `${prefix}/${page.referenceNum}/duplicate`,
            },
          },
        ];
      }

      if (Object.keys(menuGroups).length === 1) {
        return Object.values(menuGroups)[0];
      }

      return menuGroups;
    }
    return [];
  }

  remoteLinkAttributes(records): RemoteAttributes | undefined {
    if (records.length === 1) {
      const record = records[0];

      if (record.typename === 'Page' && record.isPersonal) {
        return {
          href: `/user/pages/${records[0].referenceNum}/duplicate`,
          method: 'post',
        };
      }

      let urlRecordName = `${record.typename.toLowerCase()}s`;
      if (record.typename === 'Idea') {
        urlRecordName = 'ideas/ideas';
      }

      // If we're in the right-click menu on a document, don't open the new record in the drawer
      let duplicateAction = 'duplicate.drawer';
      if (record.typename === 'Page') {
        duplicateAction = 'duplicate';
      } else if (record.typename === 'Idea') {
        duplicateAction = 'duplicate.js?drawer=true';
      }
      return {
        href: `/${urlRecordName}/${records[0].referenceNum}/${duplicateAction}`,
        method: 'post',
      };
    } else {
      // If we have multiple records, we need to use runForRecords
      return undefined;
    }
  }

  showForRecord(record: ApplicationModel): record is CopyRecordType {
    if (!super.showForRecord(record)) {
      return false;
    }

    return (
      // If it's a page, don't show for links
      (!(record instanceof Page) || !record.isLink) &&
      record.permissions?.duplicate !== false &&
      ([Feature, Epic, Requirement, Idea].some(
        type => record instanceof type
      ) ||
        this.selectedRecords.length === 1)
    );
  }

  multipleSelections() {
    return this.selectedRecords.length > 1;
  }

  async runForRecords(records: CopyRecordType[], data: any = {}) {
    await Promise.all(
      records
        // Filtering here to avoid unnecessary changes when using multi-select
        .filter(r => this.showForRecord(r))
        .map(async record => {
          const path =
            record.path ||
            (record.constructor as typeof ApplicationModel).pathForId(
              record.id
            );

          const params = new URLSearchParams({ multiple: 'true' });

          // Add any additional URL parameters from data
          if (data.urlParams) {
            Object.entries(data.urlParams).forEach(([key, value]) => {
              params.append(key, String(value));
            });
          }

          const url = `${path}/duplicate?${params.toString()}`;

          await jsonFetch(url, { method: 'POST' });
        })
    );

    const message = this.singleRecordType
      ? terminology(modelTerminology(records[0]).toLowerCase(), {
          isPlural: true,
        })
      : 'records';
    showNoticeMessage(
      `Your ${records.length} ${message} are being copied in the background.`
    );
  }
}
